import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By downloading, accessing or using the HYPSEYA application ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.

These Terms constitute a legally binding agreement between you and HYPSEYA, operated by Helena Solnado, based in Portugal.

You must be at least 18 years old to use the Service. By using HYPSEYA, you confirm that you meet this requirement.`
  },
  {
    id: 'service',
    title: '2. Description of Service',
    content: `HYPSEYA is an emotional training platform designed to support emotional regulation and self-awareness through:

• Daily Loop — a four-phase protocol (Orient, Practice, Observe, Integrate)
• Personalised AI-generated meditation scripts
• Voice cloning for meditation narration (optional, via Voice Lab)
• Emotional Regulation Index (ERI) — a longitudinal metric tracking your regulation capacity, stability, and practice consistency
• Clinical assessment tools (PHQ-9, GAD-7) for baseline awareness

HYPSEYA is a wellness tool and is not a medical device, therapeutic treatment, or substitute for professional mental health care. It does not provide diagnoses, clinical advice, or emergency support.`
  },
  {
    id: 'health',
    title: '3. Health & Wellness Disclaimer',
    content: `HYPSEYA is designed to support emotional well-being and self-regulation. It is not a medical product and should not be used as a replacement for professional psychiatric, psychological, or medical care.

If you are experiencing a mental health crisis, thoughts of self-harm, or a medical emergency, please contact emergency services or a qualified mental health professional immediately.

Emergency contacts:
• Portugal: SNS 24 — 808 24 24 24
• EU Crisis Line: findahelpline.com
• International: befrienders.org

By using HYPSEYA, you acknowledge that:
• The ERI and related metrics are wellness indicators, not clinical diagnoses
• AI-generated content is not a substitute for personalised professional guidance
• You are responsible for your own health decisions`
  },
  {
    id: 'account',
    title: '4. Your Account',
    content: `To access HYPSEYA, you must create an account. You are responsible for:

• Maintaining the confidentiality of your login credentials
• All activity that occurs under your account
• Providing accurate, current information

You may not share your account with others or use another person's account without permission.

We reserve the right to suspend or terminate accounts that violate these Terms, engage in abusive behaviour, or are used for fraudulent purposes.`
  },
  {
    id: 'content',
    title: '5. User Content & Voice Data',
    content: `When you use the Voice Lab feature, you provide a voice recording to create a personalised voice model. By doing so, you:

• Grant HYPSEYA a limited licence to process your voice data solely for the purpose of generating your personalised meditation narrations
• Acknowledge that your voice data is transmitted to ElevenLabs (our voice synthesis processor) under a signed Data Processing Agreement
• Confirm that you are the only person whose voice is being recorded

You retain ownership of your voice. Your voice model is deleted when you delete your account.

Daily Loop entries, ERI data, and meditation content you generate remain private to your account and are never shared with third parties except as described in our Privacy Policy.`
  },
  {
    id: 'ai',
    title: '6. AI-Generated Content',
    content: `HYPSEYA uses OpenAI GPT-4o to generate personalised meditation scripts based on your emotional state and Daily Loop input.

You acknowledge that:
• AI-generated content may not always be perfectly suited to your individual needs
• HYPSEYA is not responsible for outcomes resulting from following AI-generated guidance
• You may skip, stop, or disregard any generated content at any time

Meditation scripts are generated using pseudonymised data — your name and email are never sent to OpenAI. See our Privacy Policy for full details.`
  },
  {
    id: 'subscription',
    title: '7. Subscription & Payments',
    content: `HYPSEYA may offer free and paid subscription tiers. Where a paid subscription is offered:

• Payment is processed through Apple App Store or Google Play, subject to their respective terms
• Subscriptions auto-renew unless cancelled at least 24 hours before the end of the billing period
• You may manage or cancel your subscription at any time via your App Store account settings
• Refunds are governed by Apple and Google's refund policies

We reserve the right to modify pricing with reasonable notice.`
  },
  {
    id: 'prohibited',
    title: '8. Prohibited Uses',
    content: `You agree not to:

• Use the Service for any unlawful purpose or in violation of any applicable law
• Attempt to reverse-engineer, decompile, or extract source code from the app
• Scrape, harvest, or collect data from other users
• Use automated tools to access the Service without authorisation
• Impersonate another person or entity
• Upload malicious code or attempt to disrupt the Service
• Use the Service to harass, threaten, or harm others`
  },
  {
    id: 'ip',
    title: '9. Intellectual Property',
    content: `All content, design, methodology, and technology within HYPSEYA — including the Daily Loop protocol, ERI framework, app design, and AI-generated meditation scripts tailored to your session — are the intellectual property of HYPSEYA / Helena Solnado.

You are granted a limited, non-exclusive, non-transferable licence to use the Service for personal, non-commercial purposes.

You may not copy, distribute, modify, or create derivative works from any part of the Service without our written consent.`
  },
  {
    id: 'termination',
    title: '10. Termination',
    content: `You may delete your account at any time via the Account Settings page within the app. Upon deletion, all your personal data is permanently removed within 24 hours, in accordance with our Privacy Policy.

We may suspend or terminate your access if you breach these Terms. Where reasonable, we will give notice before termination.`
  },
  {
    id: 'liability',
    title: '11. Limitation of Liability',
    content: `To the fullest extent permitted by applicable law, HYPSEYA and Helena Solnado shall not be liable for:

• Any indirect, incidental, or consequential damages arising from your use of the Service
• Decisions made based on ERI scores, AI-generated content, or any data within the app
• Service interruptions, data loss, or technical failures beyond our reasonable control

Nothing in these Terms limits liability for death, personal injury, or fraud caused by our negligence.`
  },
  {
    id: 'changes',
    title: '12. Changes to These Terms',
    content: `We may update these Terms from time to time. When we do, we will notify you via the app or email with reasonable notice before changes take effect.

Your continued use of the Service after changes are notified constitutes acceptance of the updated Terms. If you do not agree to the updated Terms, you may delete your account.`
  },
  {
    id: 'governing',
    title: '13. Governing Law',
    content: `These Terms are governed by and construed in accordance with the laws of Portugal. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of Portugal.

If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.`
  },
  {
    id: 'contact',
    title: '14. Contact',
    content: `For questions about these Terms, please contact:

Helena Solnado — HYPSEYA
Email: helena@hypseya.com
Website: hypseya.com

Registered in Portugal`
  },
];

export default function Terms() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F5F0 0%, #EAF1F0 100%)',
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: '32px 24px 24px',
        maxWidth: '720px',
        margin: '0 auto',
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#1E6B68', fontSize: '0.85rem', padding: '0',
            display: 'flex', alignItems: 'center', gap: '6px',
            marginBottom: '28px', fontWeight: '500',
          }}
        >
          ← Back
        </button>

        <div style={{ marginBottom: '8px' }}>
          <span style={{
            fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.14em',
            textTransform: 'uppercase', color: '#2D8B87',
          }}>
            Legal
          </span>
        </div>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.8rem, 5vw, 2.4rem)',
          color: '#1E3A39', margin: '0 0 8px', fontWeight: '500',
        }}>
          Terms of Service
        </h1>
        <p style={{ color: '#8FA3A0', fontSize: '0.85rem', margin: '0 0 6px' }}>
          Effective date: 28 April 2026
        </p>
        <p style={{ color: '#8FA3A0', fontSize: '0.82rem', margin: 0 }}>
          Please read these Terms carefully before using HYPSEYA.
        </p>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px 80px' }}>
        {SECTIONS.map((section) => {
          const isOpen = active === section.id;
          return (
            <div
              key={section.id}
              style={{
                background: 'rgba(255,255,255,0.88)',
                borderRadius: '16px',
                marginBottom: '10px',
                border: isOpen
                  ? '1.5px solid rgba(30,107,104,0.25)'
                  : '1.5px solid rgba(184,211,207,0.3)',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease',
              }}
            >
              <button
                onClick={() => setActive(isOpen ? null : section.id)}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '18px 20px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <span style={{
                  fontSize: '0.92rem', fontWeight: '600',
                  color: isOpen ? '#1E6B68' : '#1E3A39',
                }}>
                  {section.title}
                </span>
                <span style={{
                  color: '#2D8B87', fontSize: '1rem',
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.25s ease',
                  flexShrink: 0, marginLeft: '12px',
                }}>
                  ⌄
                </span>
              </button>

              {isOpen && (
                <div style={{ padding: '0 20px 20px' }}>
                  <div style={{
                    height: '1px',
                    background: 'rgba(184,211,207,0.4)',
                    marginBottom: '16px',
                  }} />
                  <p style={{
                    fontSize: '0.88rem', color: '#4A5E5D',
                    lineHeight: '1.75', margin: 0,
                    whiteSpace: 'pre-line',
                  }}>
                    {section.content}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
